import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar, CheckCircle2, Circle, Clock, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { Avatar, AvatarFallback } from "@/components/avatar";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/dialog";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { Layout } from "@/components/layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { Textarea } from "@/components/textarea";
import {
  type Customer,
  formatRelativeDate,
  getInitials,
  type Task,
  type TaskPriority,
} from "@/lib/types";

// Color palette for avatar backgrounds - Fresh design
const avatarColors = [
  "bg-indigo-100 text-indigo-700",
  "bg-violet-100 text-violet-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
];

function getAvatarColor(name: string): string {
  const index =
    name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % avatarColors.length;
  return avatarColors[index];
}

const emptyTask = {
  title: "",
  description: "",
  due_date: "",
  priority: "normal" as TaskPriority,
  customer_id: "",
};

export function Tasks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = React.useState("today");
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(searchParams.get("new") === "true");
  const [formData, setFormData] = React.useState(emptyTask);

  // Fetch tasks
  const { data: tasks = [], isLoading } = useQuery<Task[]>({
    queryKey: ["tasks"],
    queryFn: async () => {
      const res = await fetch("/api/tasks");
      if (!res.ok) throw new Error("Failed to fetch tasks");
      return res.json();
    },
  });

  // Fetch customers for dropdown
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ["customers"],
    queryFn: async () => {
      const res = await fetch("/api/customers");
      if (!res.ok) throw new Error("Failed to fetch customers");
      return res.json();
    },
  });

  // Create task mutation
  const createTask = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch("/api/tasks", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          customer_id: data.customer_id ? parseInt(data.customer_id) : null,
        }),
      });
      if (!res.ok) throw new Error("Failed to create task");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setIsAddDialogOpen(false);
      setFormData(emptyTask);
      setSearchParams({});
    },
  });

  // Toggle task completion mutation
  const toggleTask = useMutation({
    mutationFn: async ({ id, completed }: { id: number; completed: boolean }) => {
      const res = await fetch(`/api/tasks/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed }),
      });
      if (!res.ok) throw new Error("Failed to update task");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  // Delete task mutation
  const deleteTask = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/tasks/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete task");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  // Filter tasks by tab
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
  const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

  const filteredTasks = tasks.filter((task) => {
    const dueDate = new Date(task.due_date);

    switch (activeTab) {
      case "today":
        return !task.completed && dueDate >= today && dueDate < tomorrow;
      case "upcoming":
        return !task.completed && dueDate >= tomorrow && dueDate < nextWeek;
      case "overdue":
        return !task.completed && dueDate < today;
      case "completed":
        return task.completed;
      default:
        return true;
    }
  });

  // Sort tasks by due date and priority
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    // Priority order: urgent > normal > low
    const priorityOrder = { urgent: 0, normal: 1, low: 2 };
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;

    // Then by due date
    return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
  });

  // Calculate counts
  const counts = {
    today: tasks.filter((t) => {
      const dueDate = new Date(t.due_date);
      return !t.completed && dueDate >= today && dueDate < tomorrow;
    }).length,
    upcoming: tasks.filter((t) => {
      const dueDate = new Date(t.due_date);
      return !t.completed && dueDate >= tomorrow && dueDate < nextWeek;
    }).length,
    overdue: tasks.filter((t) => {
      const dueDate = new Date(t.due_date);
      return !t.completed && dueDate < today;
    }).length,
    completed: tasks.filter((t) => t.completed).length,
  };

  // Set default due date to today at 5pm
  React.useEffect(() => {
    if (isAddDialogOpen) {
      setFormData((f) => {
        if (!f.due_date) {
          const defaultDate = new Date();
          defaultDate.setHours(17, 0, 0, 0);
          return {
            ...f,
            due_date: defaultDate.toISOString().slice(0, 16),
          };
        }
        return f;
      });
    }
  }, [isAddDialogOpen]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">งาน & การติดตาม</h1>
            <p className="mt-1 text-slate-500">ติดตามงานและการนัดหมายลูกค้า</p>
          </div>
          <Button
            className="rounded-xl bg-indigo-500 hover:bg-indigo-600"
            onClick={() => setIsAddDialogOpen(true)}
          >
            <Plus className="mr-2 size-4" />
            เพิ่มงาน
          </Button>
        </div>

        {/* Stats */}
        <div className="grid gap-4 sm:grid-cols-4">
          <Card
            className={`cursor-pointer rounded-2xl border-slate-200 shadow-sm transition-all ${activeTab === "today" ? "ring-2 ring-indigo-500" : "hover:border-indigo-200"}`}
            onClick={() => setActiveTab("today")}
          >
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-indigo-50">
                <Calendar className="size-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">วันนี้</p>
                <p className="text-2xl font-bold text-indigo-600">{counts.today}</p>
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer rounded-2xl border-slate-200 shadow-sm transition-all ${activeTab === "upcoming" ? "ring-2 ring-indigo-500" : "hover:border-violet-200"}`}
            onClick={() => setActiveTab("upcoming")}
          >
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-violet-50">
                <Clock className="size-6 text-violet-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">กำลังจะถึง</p>
                <p className="text-2xl font-bold text-violet-600">{counts.upcoming}</p>
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer rounded-2xl border-slate-200 shadow-sm transition-all ${activeTab === "overdue" ? "ring-2 ring-indigo-500" : "hover:border-red-200"}`}
            onClick={() => setActiveTab("overdue")}
          >
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-red-50">
                <Clock className="size-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">เลยกำหนด</p>
                <p className="text-2xl font-bold text-red-600">{counts.overdue}</p>
              </div>
            </CardContent>
          </Card>

          <Card
            className={`cursor-pointer rounded-2xl border-slate-200 shadow-sm transition-all ${activeTab === "completed" ? "ring-2 ring-indigo-500" : "hover:border-emerald-200"}`}
            onClick={() => setActiveTab("completed")}
          >
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex size-12 items-center justify-center rounded-xl bg-emerald-50">
                <CheckCircle2 className="size-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-500">เสร็จแล้ว</p>
                <p className="text-2xl font-bold text-emerald-600">{counts.completed}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs and Task List */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start rounded-xl bg-slate-100 p-1">
            <TabsTrigger value="today" className="gap-2 rounded-lg data-[state=active]:bg-white">
              วันนี้
              {counts.today > 0 && (
                <span className="ml-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700">
                  {counts.today}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="gap-2 rounded-lg data-[state=active]:bg-white">
              กำลังจะถึง
              {counts.upcoming > 0 && (
                <span className="ml-1 rounded-full bg-violet-100 px-1.5 py-0.5 text-xs font-medium text-violet-700">
                  {counts.upcoming}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="overdue" className="gap-2 rounded-lg data-[state=active]:bg-white">
              เลยกำหนด
              {counts.overdue > 0 && (
                <span className="ml-1 rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                  {counts.overdue}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="completed" className="rounded-lg data-[state=active]:bg-white">
              เสร็จแล้ว
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base font-semibold text-slate-900">
                  {activeTab === "today"
                    ? "งานวันนี้"
                    : activeTab === "upcoming"
                      ? "งานที่กำลังจะถึง"
                      : activeTab === "overdue"
                        ? "งานที่เลยกำหนด"
                        : "งานที่เสร็จแล้ว"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="flex animate-pulse items-center gap-4 rounded-xl bg-slate-50 p-4"
                      >
                        <div className="size-6 rounded-full bg-slate-100" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-48 rounded bg-slate-100" />
                          <div className="h-3 w-32 rounded bg-slate-100" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : sortedTasks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="flex size-14 items-center justify-center rounded-2xl bg-slate-100">
                      <Clock className="size-7 text-slate-400" />
                    </div>
                    <p className="mt-3 text-sm font-medium text-slate-600">
                      {activeTab === "completed" ? "ยังไม่มีงานที่เสร็จ" : "ไม่มีงานในส่วนนี้"}
                    </p>
                    <p className="text-xs text-slate-400">
                      {activeTab === "completed"
                        ? "ทำงานเสร็จแล้วจะแสดงที่นี่"
                        : activeTab === "overdue"
                          ? "ดีมาก! ไม่มีงานที่เลยกำหนด"
                          : "เพิ่มงานเพื่อเริ่มต้น"}
                    </p>
                    {activeTab !== "completed" && activeTab !== "overdue" && (
                      <Button
                        className="mt-4 rounded-xl bg-indigo-500 hover:bg-indigo-600"
                        size="sm"
                        onClick={() => setIsAddDialogOpen(true)}
                      >
                        <Plus className="mr-2 size-4" />
                        เพิ่มงาน
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {sortedTasks.map((task) => (
                      <div
                        key={task.id}
                        className={`group flex items-center gap-4 rounded-xl border p-4 transition-all ${
                          task.completed
                            ? "border-slate-100 bg-slate-50"
                            : "border-slate-200 bg-white hover:border-indigo-200"
                        }`}
                      >
                        <button
                          onClick={() =>
                            toggleTask.mutate({
                              id: task.id,
                              completed: !task.completed,
                            })
                          }
                          className="flex-shrink-0"
                        >
                          {task.completed ? (
                            <CheckCircle2 className="size-6 text-emerald-600" />
                          ) : (
                            <Circle
                              className={`size-6 ${
                                task.priority === "urgent"
                                  ? "text-red-400"
                                  : task.priority === "normal"
                                    ? "text-indigo-400"
                                    : "text-slate-300"
                              }`}
                            />
                          )}
                        </button>

                        <div className="min-w-0 flex-1">
                          <p
                            className={`font-medium ${
                              task.completed ? "text-slate-400 line-through" : "text-slate-900"
                            }`}
                          >
                            {task.title}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className={activeTab === "overdue" ? "text-red-600" : ""}>
                              {formatRelativeDate(task.due_date)}
                            </span>
                            {task.customer && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Avatar className="size-4">
                                    <AvatarFallback
                                      className={`text-[8px] ${getAvatarColor(task.customer.name)}`}
                                    >
                                      {getInitials(task.customer.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  {task.customer.name}
                                </span>
                              </>
                            )}
                          </div>
                          {task.description && (
                            <p className="mt-1 text-sm text-slate-400">{task.description}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              task.priority === "urgent"
                                ? "priority-urgent"
                                : task.priority === "normal"
                                  ? "priority-normal"
                                  : "priority-low"
                            }
                          >
                            {task.priority === "urgent"
                              ? "ด่วน"
                              : task.priority === "normal"
                                ? "ปกติ"
                                : "ต่ำ"}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 rounded-lg opacity-0 group-hover:opacity-100"
                            onClick={() => deleteTask.mutate(task.id)}
                          >
                            <Trash2 className="size-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Task Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={(open) => {
          setIsAddDialogOpen(open);
          if (!open) {
            setSearchParams({});
            setFormData(emptyTask);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>เพิ่มงานใหม่</DialogTitle>
            <DialogDescription>สร้างงานติดตามลูกค้าหรือการแจ้งเตือน</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createTask.mutate(formData);
            }}
            className="space-y-4"
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">ชื่องาน *</Label>
                <Input
                  id="title"
                  placeholder="ติดตามลูกค้า"
                  className="rounded-xl border-slate-200"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">รายละเอียด</Label>
                <Textarea
                  id="description"
                  placeholder="รายละเอียดเพิ่มเติม..."
                  className="rounded-xl border-slate-200"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="due_date">วันครบกำหนด *</Label>
                  <Input
                    id="due_date"
                    type="datetime-local"
                    className="rounded-xl border-slate-200"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="priority">ความสำคัญ</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value: TaskPriority) =>
                      setFormData({ ...formData, priority: value })
                    }
                  >
                    <SelectTrigger className="rounded-xl border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">ด่วน</SelectItem>
                      <SelectItem value="normal">ปกติ</SelectItem>
                      <SelectItem value="low">ต่ำ</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="customer">ลูกค้าที่เกี่ยวข้อง (ไม่บังคับ)</Label>
                <Select
                  value={formData.customer_id || "none"}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, customer_id: value === "none" ? "" : value })
                  }
                >
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue placeholder="เลือกลูกค้า" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">ไม่ระบุ</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id.toString()}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setSearchParams({});
                }}
              >
                ยกเลิก
              </Button>
              <Button
                type="submit"
                className="rounded-xl bg-indigo-500 hover:bg-indigo-600"
                disabled={createTask.isPending}
              >
                {createTask.isPending ? "กำลังเพิ่ม..." : "เพิ่มงาน"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
